import type { ColumnDef } from "@tanstack/react-table";
import type { RotationsRow, WindowStatus } from "@/types/tables";

export const formatWindow = (w: { from: Date; to: Date }) => {
  const from = new Date(w?.from);
  const to = new Date(w?.to);

  const fmt = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${fmt.format(from)} → ${fmt.format(to)}`;
};

export const getWindowStatus = (w: { from: Date; to: Date }): WindowStatus => {
  const now = new Date();
  const from = new Date(w?.from);
  const to = new Date(w?.to);

  if (now < from) return "soon";
  if (now > to) return "passed";
  return "active";
};

export const statusLabel: Record<WindowStatus, string> = {
  soon: "Soon",
  active: "Active",
  passed: "Passed",
};

export const statusVariant: Record<WindowStatus, "success" | "outline"> = {
  soon: "outline",
  active: "success",
  passed: "outline",
};

export const windowFilterFn = (
  row: any,
  columnId: string,
  filterValue: "soon" | "active" | "passed" | "all",
) => {
  if (!filterValue || filterValue.length === 0) return true;

  const w = row.getValue(columnId) as { from: Date; to: Date };
  const status = getWindowStatus(w);
  return filterValue.includes(status);
};

export const sortByWindowFrom: ColumnDef<RotationsRow>["sortingFn"] = (
  rowA,
  rowB,
  columnId,
) => {
  const a = rowA.getValue(columnId) as { from: Date; to: Date };
  const b = rowB.getValue(columnId) as { from: Date; to: Date };

  const aTime = new Date(a.from).getTime();
  const bTime = new Date(b.from).getTime();

  return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
};

export const withLocalTime = (date: Date, time: string) => {
  const [hh, mm, ss] = time.split(":").map(Number);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hh ?? 0,
    mm ?? 0,
    ss ?? 0,
    0,
  );
};

export const toLocalTimeString = (d: Date) => {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};
